import { AxiosInstance } from "axios"
import { ExternalServiceEntry } from "./extraServicesTypes"
import type { NoPriceListId } from "../priceList/priceListApi"
import { ServiceResponse } from "../../../../core/api/apiTypes"

export enum ExtraServicesQuery {
    LIST = "extra-services-merchant",
    DETAILS = "extra-services-details-merchant",
    DELETE = "delete-extra-services-merchant",
}

// -- Fetch Extra Services --
type FetchExtraServicesParams = Readonly<{
    merchantId: number
    priceListId?: number | NoPriceListId | null
}>

export type FetchExtraServicesResponse = {
    extraServices: ExternalServiceEntry[]
}

export async function fetchExtraServices(
    { merchantId, priceListId }: FetchExtraServicesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchExtraServicesResponse> {
    const url = `/merchants/${merchantId}/extraServices`

    const response = await axios.get(url, {
        params: { priceListId },
        signal,
    })

    return response.data
}

// -- Fetch Extra Service --
type FetchExtraServiceParams = Readonly<{
    extraServiceId: number
}>

export type FetchProductsResponse = {
    product: ExternalServiceEntry
}

export async function fetchExtraServiceDetails(
    { extraServiceId }: FetchExtraServiceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchProductsResponse> {
    const url = `/products/${extraServiceId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// -- Create Extra Service --
type CreateExtraServiceParams = { product: ExternalServiceEntry }
type CreateExtraServiceResponse = ServiceResponse<{}>

export async function createExtraService(
    params: CreateExtraServiceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateExtraServiceResponse> {
    const { product } = params
    const response = await axios.post(
        "/extraServices",
        {
            extraService: product,
        },
        {
            signal,
        },
    )
    return response.data
}

// -- Create Extra Service --
export type UpdateExtraServiceBody = ExternalServiceEntry & {
    extraServiceId: number
}

type UpdateExtraServiceParams = {
    productId: number
    product: ExternalServiceEntry
}
type UpdateExtraServiceResponse = ServiceResponse<{}>

export async function updateExtraService(
    params: UpdateExtraServiceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateExtraServiceResponse> {
    const { product, productId } = params
    const response = await axios.post(
        `/extraServices/${productId}`,
        {
            extraService: product,
        },
        {
            signal,
        },
    )
    return response.data
}

// -- Delete Extra Service --
type DeleteExtraServiceParams = Readonly<{
    productId: number
}>

export type DeleteExtraServiceResponse = ServiceResponse<{}>

export async function deleteExtraService(
    params: DeleteExtraServiceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeleteExtraServiceResponse> {
    const { productId } = params
    const url = `/extraServices/${productId}`

    const response = await axios.delete(url, {
        signal,
    })

    return response.data
}

// -- Get extra service details --
type GetExtraServiceDetailsParams = Readonly<{
    productId: number
}>

export type GetExtraServiceDetailsResponse = {
    extraService: ExternalServiceEntry
}

export async function getProductDetails(
    params: GetExtraServiceDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetExtraServiceDetailsResponse> {
    const { productId } = params
    const url = `/extraServices/${productId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// -- Sort extra service in price list --

export type SortExtraServiceInPriceListBody = Readonly<{
    moveTo: {
        position: number
    }
}>

type SortExtraServiceInPriceListParams = Readonly<{
    priceListId: number
    extraServiceId: number
    body: SortExtraServiceInPriceListBody
}>

export async function sortExtraServiceInPriceList(
    params: SortExtraServiceInPriceListParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { priceListId, extraServiceId, body } = params
    const response = await axios.post(`/priceLists/${priceListId}/extraServices/${extraServiceId}/sort`, body, {
        signal,
    })

    return response.data
}
